import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../services/Api/Admin/adminApi";
import { partnerApi } from "../../../../services/Api/Partner/partnerApi";
import {
	OrganizationProductApi,
	OrganizationProductFormApi,
	OrganizationProductFormProductApi,
	SearchSelectApi,
} from "../../../../services/Api/types";

interface Props {
	products: OrganizationProductApi[];
	organizationId: number;
	possibleProducts: SearchSelectApi[];
	partnerId?: number;
}

const parseFormDefaultValues = (
	products: OrganizationProductApi[],
	possibleProducts: SearchSelectApi[]
): OrganizationProductFormProductApi[] => {
	const result: OrganizationProductFormProductApi[] = [];

	possibleProducts.forEach((possibleProduct) => {
		const product = products.find(({ name }) => possibleProduct.id.toString().toLowerCase() === name.toLowerCase());
		if (!product) {
			result.push({
				value: false,
				partner: null,
				partner_id: null,
				name: possibleProduct.label,
			});
		} else {
			result.push({
				value: true,
				partner: product.partner ?? null,
				partner_id: product.partner_id ?? null,
				name: product.name,
			});
		}
	});

	return result;
};

const OrganizationProductsTableForm = ({ products, organizationId, possibleProducts, partnerId }: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<OrganizationProductFormApi>({
		criteriaMode: "all",
		defaultValues: {
			products: parseFormDefaultValues(products, possibleProducts),
		},
	});
	const {
		formState,
		formState: { errors },
		control,
		watch,
		setValue,
		handleSubmit,
		setError,
		reset,
		register,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const isMobile = useWindowSize().isMobile;
	const [productsUpdateLoading, setProductsUpdateLoading] = useState(false);

	const searchPartner = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return adminApi.getAdminPartnersSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const getParsedFormData = (data: OrganizationProductFormApi) => {
		return data.products.filter((product) => product.value);
	};

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		const newData = { products: getParsedFormData(data) };
		try {
			const res = partnerId
				? await partnerApi.updateOrganizationProducts(partnerId, organizationId, newData)
				: await adminApi.updateOrganizationProducts(organizationId, newData);
			reset({
				products: [...parseFormDefaultValues(res, possibleProducts)],
			});
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const handleUpdateProducts = async () => {
		setProductsUpdateLoading(true);
		try {
			partnerId
				? await partnerApi.rewriteOrganizationProducts(partnerId, organizationId)
				: await adminApi.rewriteOrganizationProducts(organizationId);
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setProductsUpdateLoading(false);
	};

	return (
		<FormDirty formState={formState} onSubmit={onSubmit}>
			<fieldset className="form-group">
				<div className={`${isMobile ? "table-responsive" : ""}`}>
					<table className="table table-form">
						<thead>
							<tr>
								<th className="action" />
								<th style={{ minWidth: isMobile ? "200px" : "100px" }}>{t("lib:common.word.name")}</th>
								{!partnerId && (
									<th style={{ minWidth: isMobile ? "200px" : "100px" }}>
										{t("common.word.partner")}
									</th>
								)}
							</tr>
						</thead>
						<tbody>
							{possibleProducts.map((product, index) => {
								const correspondingProduct = products.find(
									({ name }) => product.id.toString().toLowerCase() === name.toLowerCase()
								);

								return (
									<tr key={product.id}>
										<td className="action" />
										<td>
											<FormCheck
												name={`products.${index}.value`}
												errors={errors}
												register={register}
												label={product.label}
												type="switch"
												disabled={correspondingProduct?.subscription_value}
											/>
										</td>
										{!partnerId && (
											<td>
												<FormSelectGroup
													errors={errors}
													control={control}
													loadOptions={searchPartner}
													name={`products.${index}.partner_id`}
													getOptionValue={(option) => option.id}
													getOptionLabel={(option) => option.name}
													data-testid={`products.${index}.partner_id`}
													defaultValue={{
														name: watch(`products.${index}.partner`)?.name || null,
														id: watch(`products.${index}.partner_id`) || null,
													}}
													onChange={(_, object) =>
														setValue(`products.${index}.partner.name`, object.name)
													}
												/>
											</td>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</fieldset>
			<div className={"form-footer"}>
				<Form.Group className="form-group">
					<ButtonLoading loading={loading} type={"submit"}>
						{t("lib:common.action.save")}
					</ButtonLoading>
					{!partnerId && (
						<ButtonLoading loading={productsUpdateLoading} onClick={handleUpdateProducts}>
							{t("modules.organization.action.update_products.title")}
						</ButtonLoading>
					)}
				</Form.Group>
			</div>
		</FormDirty>
	);
};

export default OrganizationProductsTableForm;
